import { Injectable, Injector } from "@angular/core";
import { FeatureName } from "@common/ADAPT.Common.Model/embed/feature-name.enum";
import { FeaturePermissionName } from "@common/ADAPT.Common.Model/embed/feature-permission-name.enum";
import { Person } from "@common/ADAPT.Common.Model/person/person";
import { ProgressService } from "@common/progress/progress.service";
import { BaseService } from "@common/service/base.service";
import { AuthorisationService } from "@org-common/lib/authorisation/authorisation.service";
import { FeaturesService } from "@org-common/lib/features/features.service";
import { OrganisationService } from "@org-common/lib/organisation/organisation.service";
import { CommonTeamsService } from "@org-common/lib/teams/common-teams.service";
import { lastValueFrom } from "rxjs";

@Injectable({
    providedIn: "root",
})
export class JourneyProgressAuthService extends BaseService {
    public static readonly ReadJourneyProgress = "readJourneyProgress";
    public static readonly EditJourneyProgress = "editJourneyProgress";

    public constructor(
        injector: Injector,
        private commonTeamsService: CommonTeamsService,
        private progressService: ProgressService,
        private authorisationService: AuthorisationService,
        private organisationService: OrganisationService,
        private featuresService: FeaturesService,
    ) {
        super(injector);
    }

    public static registerAccess(authorisationService: AuthorisationService) {
        authorisationService.registerAccessVerifier(
            JourneyProgressAuthService.ReadJourneyProgress,
            {
                invokeToGetAccessVerifier: (injector) =>
                    getVerifyAccessToReadJourneyProgress(injector.get(JourneyProgressAuthService)),
            },
        );
        authorisationService.registerAccessVerifier(
            JourneyProgressAuthService.EditJourneyProgress,
            {
                invokeToGetAccessVerifier: (injector) =>
                    getVerifyAccessToEditJourneyProgress(injector.get(JourneyProgressAuthService)),
            },
        );

        function getVerifyAccessToReadJourneyProgress(journeyProgressAuthService: JourneyProgressAuthService) {
            return async (currentPerson: Person) => await journeyProgressAuthService.canAccessJourneyProgress(currentPerson, FeaturePermissionName.ProgressRead)
                ? Promise.resolve()
                : Promise.reject();
        }

        function getVerifyAccessToEditJourneyProgress(journeyProgressAuthService: JourneyProgressAuthService) {
            return async (currentPerson: Person) => await journeyProgressAuthService.canAccessJourneyProgress(currentPerson, FeaturePermissionName.ProgressEdit)
                ? Promise.resolve()
                : Promise.reject();
        }
    }

    public async canAccessJourneyProgress(currentPerson: Person, permission: FeaturePermissionName) {
        // if progress isn't enabled for the organisation, skip any other checks.
        // otherwise, it will always show for the coach
        if (!this.featuresService.isFeatureActive(FeatureName.Progress)) {
            return false;
        }

        if (this.authorisationService.hasActiveCoachConnection() || currentPerson.isStakeholderManager) {
            return true;
        }

        // all team members have StrategyProgressRead/Edit, but only owner/leadership teams have the feature enabled.
        // we need to check team membership as well. since the entity isn't tied to a team, anyone with the team permission can read it.
        const teams = await this.commonTeamsService.promiseToGetActiveTeamsForPersonId(currentPerson.personId);
        const personHasAccess = teams.some((team) => this.authorisationService.personHasPermission(currentPerson, permission, team));
        if (personHasAccess || currentPerson.isCoach() || currentPerson.isStakeholderManager) {
            const products = await lastValueFrom(this.progressService.getProgressProducts(this.organisationService.getOrganisationId()));
            if (products.length > 0) {
                return true;
            }
        }

        return false;
    }
}
